<template>
  <div class="mp-tcard" @click="emit('preview', templateId)">
    <span v-if="hot" class="mp-tcard__hot">热门</span>
    <span v-if="playStyleLabelText" class="mp-tcard__play">{{ playStyleLabelText }}</span>
    <span v-if="!pluginReady" class="mp-tcard__tag">未接入</span>
    <img v-if="coverUrl" class="mp-tcard__cov" :src="coverUrl" loading="lazy" alt="" />
    <div v-else class="mp-tcard__ph">{{ name }}</div>
    <div class="mp-tcard__nm">{{ name }}</div>
    <div class="mp-tcard__br">{{ brief }}</div>
    <div class="mp-tcard__mask">
      <div class="mp-tcard__actions">
        <button
          class="mp-tcard__use"
          type="button"
          :class="{ 'is-disabled': !pluginReady }"
          :disabled="!pluginReady"
          :title="useTitle"
          @click.stop="onUse"
        >
          {{ pluginReady ? '立即使用' : '暂未接入' }}
        </button>
        <button class="mp-tcard__prev" type="button" @click.stop="emit('preview', templateId)">预览</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useMarketPortalStore } from '../useMarketPortalStore'
import { playStyleLabel } from '../playStyles'

defineOptions({ name: 'MarketTemplateCard' })

const props = defineProps<{
  templateId: number
  hot?: boolean
}>()

const emit = defineEmits<{
  preview: [id: number]
  use: [id: number]
}>()

const { getTemplateName, getTemplateBrief, getCoverUrl, isPluginReady, getNotReadyReason, getPlayStyle } =
  useMarketPortalStore()

const name = computed(() => getTemplateName(props.templateId))
const brief = computed(() => getTemplateBrief(props.templateId))
const coverUrl = computed(() => getCoverUrl(props.templateId))
const pluginReady = computed(() => isPluginReady(props.templateId))
const playStyleLabelText = computed(() => {
  if (!pluginReady.value) return ''
  return playStyleLabel(getPlayStyle(props.templateId))
})
const useTitle = computed(() =>
  pluginReady.value ? '从此模板创建活动' : getNotReadyReason(props.templateId) || '该模板玩法尚未接入'
)

const onUse = () => {
  if (!pluginReady.value) return
  emit('use', props.templateId)
}
</script>

<style lang="scss" scoped>
.mp-tcard {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #f7f8fa;

  &__hot {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
    background: linear-gradient(90deg, #ff6c6c, #ff9d5c);
    color: #fff;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 8px 8px 8px 0;
  }

  &__play {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    background: rgb(43 109 232 / 88%);
    color: #fff;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 8px;
    max-width: calc(100% - 16px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__tag {
    position: absolute;
    top: 36px;
    right: 8px;
    z-index: 2;
    background: rgb(0 0 0 / 55%);
    color: #fff;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 8px;
  }

  &__cov {
    width: 100%;
    aspect-ratio: 10 / 7;
    object-fit: cover;
    display: block;
    background: #eceef2;
  }

  &__ph {
    width: 100%;
    aspect-ratio: 10 / 7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
    padding: 8px;
    line-height: 1.4;
    background: linear-gradient(135deg, #5b8def, #8b6df0);
  }

  &__nm {
    font-size: 13px;
    color: #303133;
    margin-top: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
    padding: 0 4px;
  }

  &__br {
    font-size: 12px;
    color: #a5a8b0;
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 4px 8px;
  }

  &__mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: calc(100% - 44px);
    background: rgb(0 0 0 / 45%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover &__mask {
    opacity: 1;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    max-width: 100%;
    padding: 0 8px;
  }

  &__use,
  &__prev {
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__use {
    background: #2b6de8;
    color: #fff;
    border-radius: 15px;
    padding: 6px 16px;
    font-size: 13px;
    border: none;
    cursor: pointer;

    &.is-disabled,
    &:disabled {
      background: #909399;
      cursor: not-allowed;
    }
  }

  &__prev {
    background: #fff;
    color: #333;
    border-radius: 15px;
    padding: 6px 16px;
    font-size: 13px;
    border: none;
    cursor: pointer;
  }
}
</style>
